import { BasisAdjustmentsTableRow } from "features/fees/post/use-breakdown/FeePostUseBreakdownBasisAdjustmentsTable";
import { getFeeChargeEventBasisChangeByUseRetrieveQueryKey } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { getFeeStatsRetrieveQueryKey } from "gen/endpoints/fee-stats/fee-stats";
import { useFundFamilyEventPendingToCompleteUpdate } from "gen/endpoints/fund-family-event/fund-family-event";
import {
  getGetDraftPortfolioEventQueryKey,
  getPortfolioTransactionListAdjustmentsTableQueryKey,
  getPortfolioTransactionListQueryKey,
  getPortfolioTransactionReadOnlyTableRetriveQueryKey,
  useGetDraftPortfolioEventSuspense,
  usePortfolioTransactionUpsert,
} from "gen/endpoints/portfolio-transactions/portfolio-transactions";
import { CeleryWorkflowStateMachineFieldsEnum } from "gen/model/celeryWorkflowStateMachineFieldsEnum";
import { compact, get, noop } from "lodash-es";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MaybernTableGetErrorForCellFn } from "shared/components/table/maybern/MaybernTableErrors";
import { useToast } from "shared/components/toast/useToast";
import { useCeleryWorkflowPoll } from "shared/hooks/useCeleryWorkflowPoll";
import { getApiErrorFromResponse } from "shared/utils/ErrorUtils";
import { useInvalidateQueryKeys } from "shared/utils/QueryUtils";

export const useRefresh = ({
  fundFamilyId,
  showToasts = false,
  feeChargeEventSelected,
}: {
  fundFamilyId: string;
  showToasts?: boolean;
  feeChargeEventSelected: string;
}) => {
  const portfolioTxDraftEvent = useGetDraftPortfolioEventSuspense(fundFamilyId);
  const { t } = useTranslation(["fees", "common"]);
  const invalidateQueryKeys = useInvalidateQueryKeys();
  const { mutateAsync: mutateFundFamilyEventState } =
    useFundFamilyEventPendingToCompleteUpdate({
      mutation: {
        onSuccess: async () => {
          await invalidateQueryKeys(
            getFeeStatsRetrieveQueryKey(fundFamilyId),
            getPortfolioTransactionReadOnlyTableRetriveQueryKey(fundFamilyId),
            getPortfolioTransactionListQueryKey(fundFamilyId),
            getGetDraftPortfolioEventQueryKey(fundFamilyId),
          );
        },
      },
    });

  const {
    mutateAsync: mutatePortfolioTransactionUpsert,
    error: upsertError,
    variables: upsertVariables,
    isPending: isUpsertPending,
  } = usePortfolioTransactionUpsert({
    mutation: {
      onSuccess: async () => {
        await invalidateQueryKeys(
          getFeeStatsRetrieveQueryKey(fundFamilyId),
          getPortfolioTransactionReadOnlyTableRetriveQueryKey(fundFamilyId),
          getPortfolioTransactionListQueryKey(fundFamilyId),
          getGetDraftPortfolioEventQueryKey(fundFamilyId),
        );
      },
    },
  });

  const workflowId = portfolioTxDraftEvent.data.draft_tx?.celery_workflow_id;

  const { showToast } = useToast();
  const cache = useCeleryWorkflowPoll(
    compact([workflowId]),
    noop,
    async () => {
      await invalidateQueryKeys(
        getPortfolioTransactionListAdjustmentsTableQueryKey(fundFamilyId),
      );
      if (!showToasts) return;
      showToast({
        message: t("fees:feeUseBreakdown.basisAdjustmentsTable.toasts.success"),
        variant: "success",
      });
    },
    () => {
      if (!showToasts) return;
      showToast({
        message: t("fees:feeUseBreakdown.basisAdjustmentsTable.toasts.failure"),
        variant: "error",
      });
    },
  );
  const isCeleryWorkflowProcessing =
    (workflowId && !cache.get(workflowId)) ||
    (workflowId &&
      cache.get(workflowId)?.state ===
        CeleryWorkflowStateMachineFieldsEnum.processing);

  const isRefreshLoading =
    isUpsertPending ||
    isCeleryWorkflowProcessing ||
    portfolioTxDraftEvent.isFetching;

  const getErrorForCell = useCallback<
    MaybernTableGetErrorForCellFn<BasisAdjustmentsTableRow>
  >(
    (params) => {
      const errorData = getApiErrorFromResponse(upsertError)?.data;
      if (!errorData) {
        return undefined;
      }

      // ensure that errors are displayed in the correct row after other rows are added or removed
      const indexWhenSubmitted =
        upsertVariables?.data.portfolio_transactions.findIndex(
          // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
          (row) => get(row, "id") === params.node.id,
        ) ?? -1;
      if (indexWhenSubmitted === -1) {
        return undefined;
      }

      const key = `portfolio_transactions.${indexWhenSubmitted}.${params.column!.getColId()}`;

      // don't show error if the value has changed since submission
      if (upsertVariables) {
        const submittedValue = get(upsertVariables.data, key);
        const currentValue = get(
          params.node.data,
          params.column!.getColId(),
        ) as string | undefined;
        if (submittedValue !== currentValue) {
          return undefined;
        }
      }

      const fieldErrors = get(errorData, key) as string[] | undefined;
      return fieldErrors?.join(", ");
    },
    [upsertVariables, upsertError],
  );

  // adjustments are lost on save draft (they are upserted)
  const onSubmitDraft = useCallback(
    async (portTxs: BasisAdjustmentsTableRow[], onSuccess?: () => void) => {
      await mutatePortfolioTransactionUpsert({
        fundFamilyId,
        data: {
          portfolio_transactions: portTxs,
        },
        params: {
          finalize_event: false,
        },
      });
      // this kicks off the celery workflow we need to await before we can view the allocations table
      await invalidateQueryKeys(
        getGetDraftPortfolioEventQueryKey(fundFamilyId),
        getFeeChargeEventBasisChangeByUseRetrieveQueryKey(
          fundFamilyId,
          feeChargeEventSelected,
          {
            is_equalized_view: true,
            include_draft_transactions: true,
          },
        ),
      );

      onSuccess?.();
    },
    [
      mutatePortfolioTransactionUpsert,
      fundFamilyId,
      invalidateQueryKeys,
      feeChargeEventSelected,
    ],
  );

  return {
    isRefreshLoading,
    getErrorForCell,
    mutatePortfolioTransactionUpsert,
    mutateFundFamilyEventState,
    isUpsertPending,
    upsertVariables,
    upsertError,
    onSubmitDraft,
  };
};
